<template>
  <section
    v-if="questionnaires"
    class="h-full mx-4 grid grid-rows-[70%_30%] overflow-hidden"
    id="questionnairesManagement">
    <BaseTable
      class="overflow-x-auto"
      v-if="questionnaires"
      :headers="tableHeaders"
      :list="questionnaires"
      :total-records="questionnairesTotalCount"
      table-id="questionnaires-management"
      clickable
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @deleteMultiselect="onDeleteMultiselect"
      @reset-filters="onRefreshData({})"
      @add-new="onAddNewQuestionnaire"
      @table-item-clicked="onQuestionnaireClick">
      <template #filters>
        <section
          class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto space-y-4">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('questionnaire.isStandards')
            }}</label>
            <BaseToggle
              :label="$t('questionnaire.isStandards')"
              name="isStandards"
              v-model="filterIsStandards" />
          </div>
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="!item.isStandards"
            type="button"
            class="rounded-full p-1.5 text-theme-500 hover:bg-theme-50"
            @click="onPreview(item as Questionnaire)">
            <IconEyeOutline class="size-4" />
          </button>
          <button
            v-if="item.isFromManager"
            type="button"
            class="rounded-full p-1.5 text-blue-500 hover:bg-blue-50"
            @click="onCloneQuestionnaire(item as Questionnaire)"
            :title="$t('common.actions.clone')">
            <IconCopy class="size-4" />
          </button>
          <button
            v-if="item.isFromManager"
            type="button"
            class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
            @click="onDeleteRow(item as Questionnaire)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
    </BaseTable>

    <div id="questionnaire-preview" class="h-full overflow-y-auto">
      <QuestionnairePreview :can-reduce="true" :questionnaire="previewQuestionnaire" />
    </div>

    <BaseModalConfirm
      v-if="deleteRowItem && modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem.title }) }}
        {{ $t('questionnaire.deleteQuestionnaireWarning') }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconEyeOutline from '@/assets/icons/IconEyeOutline.vue';
import IconCopy from '@/assets/icons/IconCopy.vue';
import QuestionnairePreview from '@/components/questionnaires/QuestionnairePreview.vue';

import { type QstTableFilterData, type Questionnaire, eBindingType } from '@/types/Questionnaires';
import {
  getQuestionnairesWithConditions,
  getQuestionnaireById,
  removeQuestionnairesByIds,
} from '@/services/api/questionnaires';

import { sortData } from '@/utils/table';
import { isoDateToFullDate } from '@/utils/date';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
const { t: $t } = useI18n({ useScope: 'global' });

const router = useRouter();
const toastStore = useToastStore();

const questionnaires = ref<Questionnaire[]>();
const questionnairesTotalCount = ref<number>();
const filterIsStandards = ref(false);
const filterIsActive = ref<boolean | undefined>();

const tableHeaders = [
  { value: 'title', text: $t('questionnaire.title'), type: 'text', active: true },
  { value: 'description', text: $t('questionnaire.description'), type: 'text', active: true },
  {
    value: 'traderGroup',
    text: $t('questionnaire.traderSiteGroup'),
    type: 'text',
    active: true,
    isSortHidden: true,
  },
  {
    value: 'effectiveFromDate',
    text: $t('questionnaire.effectiveFrom'),
    type: 'text',
    active: true,
  },
  {
    value: 'effectiveUpToDate',
    text: $t('questionnaire.effectiveUpTo'),
    type: 'text',
    active: true,
  },
  { value: 'eBindingTypeString', text: $t('salesperson.type'), type: 'text', active: true },
  { value: 'sequence', text: $t('baseObject.sequence'), type: 'text', active: true },
  {
    value: 'mwQuestionnaireType',
    text: $t('questionnaire.questionnaireType'),
    type: 'text',
    active: true,
  },
  { value: 'isStandards', text: $t('questionnaire.isStandards'), type: 'check', active: true },
  { value: 'action.description', text: $t('questionnaire.action'), type: 'text', active: true },
  { value: 'listColumns', text: $t('questionnaire.listColumns'), type: 'text', active: true },
  {
    value: 'assortment',
    text: $t('questionnaire.assortment'),
    type: 'text',
    active: true,
    isSortHidden: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

type QueryParams = {
  page?: number;
  itemsPerPage: number;
  query?: string;
  sortColumn?: string;
  sortAscending?: boolean;
  isActive?: boolean;
  isStandards?: boolean;
};

onMounted(async () => {
  await loadData({
    itemsPerPage: 50,
    sortColumn: 'title',
    sortAscending: true,
    page: 1,
  });
});

function onApplyFilters(data: QstTableFilterData) {
  data.isStandards = filterIsStandards.value;
  data.isActive = filterIsActive.value;
  onRefreshData(data);
}

async function onRefreshData(data: QstTableFilterData) {
  const sortColumn = data.column !== undefined ? tableHeaders[data.column]?.value : 'id';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
    isStandards: data.isStandards,
  };

  await loadData(params);

  if (data.column !== undefined && data.order && questionnaires.value) {
    questionnaires.value = sortData(questionnaires.value, data.column, data.order, tableHeaders);
  }
}

function getQstBindingTypeString(eType: number): string {
  switch (eType) {
    case eBindingType.Generic:
      return 'Generic';
    case eBindingType.Material_groups:
      return 'Material groups';
    case eBindingType.Materials:
      return 'Materials';
    default:
      return 'Unknown';
  }
}

async function loadData(params: QueryParams) {
  const res = await getQuestionnairesWithConditions(params);
  if (res) {
    questionnaires.value = res.data.map((item) => ({
      ...item,
      effectiveFromDate: isoDateToFullDate(item.effectiveFromDate),
      effectiveUpToDate: isoDateToFullDate(item.effectiveUpToDate),
      eBindingTypeString:
        item.bindingType != null ? getQstBindingTypeString(item.bindingType) : undefined,
    }));
    questionnairesTotalCount.value = res.count;
  }
}

function onQuestionnaireClick(item: Questionnaire) {
  router.push({
    name: 'questionnaires-management-details',
    params: { id: item.id },
  });
}

async function onDeleteMultiselect(items: Questionnaire[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeQuestionnairesByIds(idsToDelete);
  if (!res && questionnaires.value)
    questionnaires.value = questionnaires.value.filter(
      (questionnaire) => !questionnaire.id || !idsToDelete.includes(questionnaire.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeQuestionnairesByIds([idToDelete]);
  if (!res && questionnaires.value)
    questionnaires.value = questionnaires.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<Questionnaire>();
function onDeleteRow(item: Questionnaire) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onAddNewQuestionnaire() {
  router.push({ name: 'questionnaires-management-details', params: { id: uuidv4() } });
}

const previewQuestionnaire = ref<Questionnaire | null>(null);
async function onPreview(item: Questionnaire) {
  const res = await getQuestionnaireById(item.id);
  if (res) previewQuestionnaire.value = res;
}

async function onCloneQuestionnaire(item: Questionnaire) {
  router.push({
    name: 'questionnaires-management-details',
    params: { id: uuidv4() },
    state: { parentId: item.id },
  });
}
</script>
